import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Alert, ImageTag, Layouts, Loader, NoData } from 'components'
import { head } from 'helpers'
import * as api from 'api'
import s from './styles.module.scss'

const IMAGE_HOST = process.env.REACT_APP_IMAGE_HOST
const PER_PAGE = 10

const getPhotoUrl = (photo) => {
  const key = Array.isArray(photo) ? photo[0] : photo
  if (!key) return null
  return `${IMAGE_HOST}${key}`
}

const countVerified = (references = []) => references.filter(r => r?.verified).length

export default function PendingReferences() {
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [pageNo, setPageNo] = useState(1)
  const [addFor, setAddFor] = useState('')
  const [savingRef, setSavingRef] = useState(false)
  const [verifyingId, setVerifyingId] = useState('')
  const [newRef, setNewRef] = useState({ name: '', relation: '', phone: '', note: '' })

  useEffect(() => {
    head({ title: 'Pending References | BCN' })
  }, [])

  const fetchData = async (page = 1) => {
    setLoading(true)
    try {
      const res = await api.admin.matrimonial.fetchPendingReferenceVerifications({ pageNo: page, perPage: PER_PAGE })
      if (res.code === 200) {
        setItems(res.payload?.profiles || [])
        setTotal(res.payload?.total || 0)
      } else {
        setItems([])
        setTotal(0)
        if (res.message) Alert.info(res.message)
      }
    } catch (err) {
      Alert.error(err?.response?.data?.message || err.message)
      setItems([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData(pageNo)
  }, [pageNo])

  const verify = async (profileId, reference) => {
    const verifyKey = `${profileId}-${reference._id}`
    if (verifyingId === verifyKey) return
    setVerifyingId(verifyKey)
    try {
      const adminData = JSON.parse(window.localStorage.getItem('adminData') || '{}')
      const verifierName = adminData?.firstName
        ? `${adminData.firstName} ${adminData.lastName || ''}`.trim()
        : 'Admin'

      const res = await api.admin.matrimonial.verifyReference({
        id: profileId,
        referenceId: reference._id,
        verifierName,
      })
      if (res.code === 200) {
        Alert.success('Reference verified successfully')
        fetchData(pageNo)
      } else {
        Alert.error(res.message || 'Failed to verify reference')
      }
    } catch (err) {
      Alert.error(err?.response?.data?.message || err.message)
    } finally {
      setVerifyingId('')
    }
  }

  const resetAddForm = () => {
    setAddFor('')
    setNewRef({ name: '', relation: '', phone: '', note: '' })
  }

  const saveRef = async (profileId) => {
    if (!newRef.name.trim()) {
      Alert.error('Reference name is required')
      return
    }
    if (newRef.phone && !/^\d{10}$/.test(newRef.phone.replace(/\D/g, ''))) {
      Alert.error('Please enter a valid 10-digit phone number')
      return
    }

    setSavingRef(true)
    try {
      const res = await api.admin.matrimonial.addReference({
        id: profileId,
        reference: {
          ...newRef,
          phone: newRef.phone ? newRef.phone.replace(/\D/g, '').slice(0, 10) : '',
        },
      })
      if (res.code === 201) {
        Alert.success('Reference added successfully')
        resetAddForm()
        fetchData(pageNo)
      } else {
        Alert.error(res.message || 'Failed to add reference')
      }
    } catch (err) {
      Alert.error(err?.response?.data?.message || err.message)
    } finally {
      setSavingRef(false)
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE))
  const canGoNext = pageNo < totalPages

  return (
    <div className={s.page}>
      <Layouts.Classic title="Pending Reference Verifications">
        <Link className={s.secondaryBtn} to="/admin/matrimonial/create">
          <span className="material-icons-outlined">person_add</span>
          Create Profile
        </Link>
      </Layouts.Classic>

      <div className={s.hero}>
        <p className={s.heroText}>
          Profiles need at least 2 verified references before they can be approved. Verify existing references or add missing ones.
        </p>

        <div className={s.statsRow}>
          <div className={s.statCard}>
            <span className={s.statLabel}>Pending profiles</span>
            <strong className={s.statValue}>{total}</strong>
          </div>
          <div className={s.statCard}>
            <span className={s.statLabel}>On this page</span>
            <strong className={s.statValue}>{items.length}</strong>
          </div>
          <div className={s.statCard}>
            <span className={s.statLabel}>Requirement</span>
            <strong className={s.statValue}>2 verified refs</strong>
          </div>
        </div>
      </div>

      {loading ? (
        <div className={s.loaderWrap}>
          <Loader color="var(--c-primary)" colorText="var(--c-primary)" />
        </div>
      ) : !items.length ? (
        <div className={s.emptyCard}>
          <NoData />
          <p>All profiles have the required verified references, or none are pending right now.</p>
          <Link className={s.primaryBtn} to="/admin/matrimonial/create">
            <span className="material-icons-outlined">person_add</span>
            Create New Profile
          </Link>
        </div>
      ) : (
        <div className={s.profileList}>
          {items.map(profile => {
            const refs = profile.references || []
            const verifiedCount = countVerified(refs)
            const photoUrl = getPhotoUrl(profile.photo)
            const needsMoreRefs = refs.length < 2
            const isAdding = addFor === profile._id

            return (
              <article key={profile._id} className={s.profileCard}>
                <div className={s.profileHeader}>
                  <div className={s.profileIdentity}>
                    {photoUrl ? (
                      <div className={s.profilePhotoWrap}>
                        <ImageTag src={photoUrl} alt={profile.fullName} />
                      </div>
                    ) : (
                      <div className={s.profilePhotoPlaceholder}>
                        <span className="material-icons-outlined">person</span>
                      </div>
                    )}
                    <div>
                      <h2>{profile.fullName}</h2>
                      <p className={s.profileMeta}>
                        {[profile.city, profile.state].filter(Boolean).join(', ') || 'Location not set'}
                        {profile.contactNumber ? ` · ${profile.contactNumber}` : ''}
                      </p>
                      {profile.gender && (
                        <span className={s.metaBadge}>{profile.gender}</span>
                      )}
                    </div>
                  </div>

                  <div className={s.verificationProgress}>
                    <span className={s.progressLabel}>Verified references</span>
                    <div className={s.miniProgressBar}>
                      <div
                        className={s.miniProgressFill}
                        style={{ width: `${Math.min(100, (verifiedCount / 2) * 100)}%` }}
                      />
                    </div>
                    <strong className={verifiedCount >= 2 ? s.readyText : s.pendingText}>
                      {verifiedCount}/2 {verifiedCount >= 2 ? 'Ready for approval' : 'Still pending'}
                    </strong>
                  </div>
                </div>

                <div className={s.referenceSection}>
                  <h3>References</h3>
                  <div className={s.referenceGrid}>
                    {refs.map((ref, index) => (
                      <div key={ref._id || index} className={s.referenceItem}>
                        <div className={s.referenceTop}>
                          <div>
                            <strong>{ref.name || 'Unnamed reference'}</strong>
                            {ref.relation ? <span className={s.refRelation}>{ref.relation}</span> : null}
                          </div>
                          <span className={ref.verified ? s.badgeVerified : s.badgePending}>
                            {ref.verified ? 'Verified' : 'Pending'}
                          </span>
                        </div>
                        <div className={s.refDetails}>
                          {ref.phone ? <span>📞 {ref.phone}</span> : null}
                          {ref.note ? <span>{ref.note}</span> : null}
                          {ref.verified && ref.verifierName ? (
                            <span className={s.verifiedBy}>Verified by {ref.verifierName}</span>
                          ) : null}
                        </div>
                        {!ref.verified && ref._id && (
                          <button
                            type="button"
                            className={s.primaryBtn}
                            disabled={verifyingId === `${profile._id}-${ref._id}`}
                            onClick={() => verify(profile._id, ref)}
                          >
                            <span className="material-icons-outlined">verified</span>
                            {verifyingId === `${profile._id}-${ref._id}` ? 'Verifying...' : 'Verify'}
                          </button>
                        )}
                      </div>
                    ))}

                    {needsMoreRefs && !isAdding && (
                      <button
                        type="button"
                        className={s.addRefCard}
                        onClick={() => {
                          resetAddForm()
                          setAddFor(profile._id)
                        }}
                      >
                        <span className="material-icons-outlined">add</span>
                        Add reference ({refs.length}/2)
                      </button>
                    )}
                  </div>

                  {isAdding && (
                    <div className={s.addRefForm}>
                      <h4>Add reference</h4>
                      <div className={s.addRefGrid}>
                        <label>
                          Name<span className={s.required}>*</span>
                          <input
                            placeholder="Reference name"
                            value={newRef.name}
                            onChange={e => setNewRef({ ...newRef, name: e.target.value })}
                          />
                        </label>
                        <label>
                          Relation
                          <input
                            placeholder="e.g. Friend, Relative"
                            value={newRef.relation}
                            onChange={e => setNewRef({ ...newRef, relation: e.target.value })}
                          />
                        </label>
                        <label>
                          Phone
                          <input
                            placeholder="10-digit number"
                            value={newRef.phone}
                            onChange={e => setNewRef({ ...newRef, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                            inputMode="numeric"
                          />
                        </label>
                        <label className={s.fullWidth}>
                          Note
                          <input
                            placeholder="Optional note"
                            value={newRef.note}
                            onChange={e => setNewRef({ ...newRef, note: e.target.value })}
                          />
                        </label>
                      </div>
                      <div className={s.addRefActions}>
                        <button type="button" className={s.secondaryBtn} onClick={resetAddForm}>
                          Cancel
                        </button>
                        <button
                          type="button"
                          className={s.primaryBtn}
                          disabled={savingRef}
                          onClick={() => saveRef(profile._id)}
                        >
                          {savingRef ? 'Saving...' : 'Save Reference'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </article>
            )
          })}
        </div>
      )}

      {!loading && total > PER_PAGE && (
        <div className={s.pagination}>
          <button
            type="button"
            className={s.secondaryBtn}
            disabled={pageNo === 1}
            onClick={() => setPageNo(n => Math.max(1, n - 1))}
          >
            <span className="material-icons-outlined">chevron_left</span>
            Previous
          </button>
          <span className={s.pageInfo}>Page {pageNo} of {totalPages}</span>
          <button
            type="button"
            className={s.secondaryBtn}
            disabled={!canGoNext}
            onClick={() => setPageNo(n => n + 1)}
          >
            Next
            <span className="material-icons-outlined">chevron_right</span>
          </button>
        </div>
      )}
    </div>
  )
}
